import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Select, Divider } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import DashboardService from "../../services/dashboard.service";
import localStorageService from "../../services/localStorage.service";
import { AuthContext } from "../../services/auth.service";
import LoadingScreen from "../../components/elements/LoadingScreen";
import CompanyDeactivated from "./components/CompanyDeactivated";
import HomeCard from "./components/HomeCard";
import ErrorRateGauge from "./components/ErrorRateGauge";
import TagMonitorSection, { withoutExcludedTags } from "./components/TagMonitorSection";
import DataLayerGuardSection from "./components/DataLayerGuardSection";
import QuickActions from "./components/QuickActions";
import { useTagMonitorData } from "./hooks/useTagMonitorData";
import { useDataLayerGuardData } from "./hooks/useDataLayerGuardData";
import { DATE_FILTERS, DEFAULT_DATE_FILTER, getPeriod, sumBy, getErrorRate, getDataLayerGuardTotals } from "./utils";
import { ERROR_RATE_INFO } from "./tooltips";
import "./styles.scss";

/**
 * Home page with the error rates, the quick actions and the Tag Monitor and DataLayer Guard sections
 *
 * @returns {ReactNode} Home page
 */
const Home = () => {
  const navigate = useNavigate();
  const { companyId, companyInfo: storedCompanyInfo, companyList } = useContext(AuthContext);
  // The company list has the same fields, for when the company info is not stored (yet)
  const companyInfo = storedCompanyInfo || companyList?.find((company) => String(company.id) === String(companyId));

  const isCompanyActive = companyInfo?.active !== false;
  const companyName = companyInfo?.name || "Code Cube";
  const username = localStorageService.getUserData()?.full_name;

  // Not every company has access to Tag Monitor, DataLayer Guard or server-side Tag Monitor
  const hasTagMonitor = Boolean(companyInfo?.tag_monitor);
  const hasDataLayerGuard = Boolean(companyInfo?.datalayer_guard);
  const hasServerSide = hasTagMonitor && Boolean(companyInfo?.tag_monitor_server);

  // Send the user to the login page when the session has expired
  useEffect(() => {
    (async () => {
      try {
        const session = await DashboardService.checkSession();
        if (!session) {
          console.warn(`[company ${companyId}] [Home] No session response received`);
          return;
        }

        const sessionData = await session.json();
        if (!sessionData?.session_exists) {
          localStorageService.clearLocalStorage();
          navigate("/login");
        }
      } catch (error) {
        // A failed check is only logged, the user stays on the page
        console.error(`[company ${companyId}] [Home] Session check failed:`, error);
      }
    })();
  }, []);

  // Date filter, the homepage opens on the last 7 days
  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE_FILTER);
  const period = useMemo(() => getPeriod(dateFilter), [dateFilter]);

  // Loaded once for the error rates and the sections
  const tagMonitorErrors = useTagMonitorData("errors", period, hasTagMonitor, "Home Tag Monitor");
  const dataLayerGuardData = useDataLayerGuardData(period, hasDataLayerGuard, "Home DataLayer Guard");

  // Shared by the error rate and the DataLayer Guard section, so both show the same numbers
  const dataLayerGuardTotals = useMemo(
    () => getDataLayerGuardTotals(dataLayerGuardData?.errors_timeline, dataLayerGuardData?.total_events_timeline),
    [dataLayerGuardData],
  );

  const errorRates = useMemo(() => {
    // Tag Monitor per side, without the tags excluded from monitoring like the rest of the homepage
    const tagErrorRate = (side) => {
      const tagErrors = withoutExcludedTags(tagMonitorErrors?.[`error_query_${side}`], tagMonitorErrors);
      return getErrorRate(tagMonitorErrors, sumBy(tagErrors, "total_fails"), sumBy(tagErrors, "total_calls"));
    };

    return {
      tagMonitorClient: tagErrorRate("client"),
      tagMonitorServer: tagErrorRate("server"),
      dataLayerGuard: getErrorRate(dataLayerGuardData, dataLayerGuardTotals.errors, dataLayerGuardTotals.events),
    };
  }, [tagMonitorErrors, dataLayerGuardData, dataLayerGuardTotals]);

  // The quick actions and the Tag Monitor section load their other data and report when they are loading
  const [quickActionsLoading, setQuickActionsLoading] = useState(true);
  const [tagMonitorLoading, setTagMonitorLoading] = useState(true);

  // Like on the other pages, the loading screen covers the page until all its data is loaded
  const isLoading =
    quickActionsLoading ||
    (hasTagMonitor && (tagMonitorErrors === undefined || tagMonitorLoading)) ||
    (hasDataLayerGuard && dataLayerGuardData === undefined);

  if (!isCompanyActive) return <CompanyDeactivated />;

  return (
    <div className="home-page">
      <LoadingScreen isActive={isLoading} />
      <div className="home-page-header">
        <h2 className="welcome-message">
          Welcome back {username} to the {companyName} dashboard!
        </h2>
        <Select
          className="date-filter-select"
          popupClassName="date-filter-select-dropdown"
          popupMatchSelectWidth={false}
          suffixIcon={<CaretDownOutlined />}
          value={dateFilter}
          onChange={setDateFilter}
          options={DATE_FILTERS.map(({ value, title }) => ({ value, label: title }))}
        />
      </div>
      <Divider style={{ marginBottom: "1rem" }} />
      <Row gutter={[16, 16]}>
        <Col span={10}>
          <HomeCard
            title="Error rate"
            // info={ERROR_RATE_INFO}
            viewMore={false}
          >
            <div className="error-rate-gauges">
              {hasTagMonitor && (
                <ErrorRateGauge {...errorRates.tagMonitorClient} label={<>Tag Monitor<br />client-side</>} />
              )}
              {hasServerSide && (
                <ErrorRateGauge {...errorRates.tagMonitorServer} label={<>Tag Monitor<br />server-side</>} />
              )}
              {hasDataLayerGuard && <ErrorRateGauge {...errorRates.dataLayerGuard} label="DataLayer Guard" />}
            </div>
          </HomeCard>
        </Col>
        <Col span={14}>
          <QuickActions onLoadingChange={setQuickActionsLoading} />
        </Col>
      </Row>
      {hasTagMonitor && (
        <TagMonitorSection period={period} errorsContext={tagMonitorErrors} onLoadingChange={setTagMonitorLoading} />
      )}
      {hasDataLayerGuard && (
        <DataLayerGuardSection period={period} context={dataLayerGuardData} totals={dataLayerGuardTotals} />
      )}
    </div>
  );
};

export default Home;
